import Select, { MultiValue, components, GroupBase } from 'react-select';
import { SetterOrUpdater } from 'recoil';

type SortOrClearValue = 'UP' | 'DOWN' | 'CLEAR_ALL';

type SortOrClearOption = {
  value: SortOrClearValue;
  label: string;
};

const SORT_OPTIONS: readonly SortOrClearOption[] = [
  { value: 'UP', label: 'Sort A - Z' },
  { value: 'DOWN', label: 'Sort Z - A' },
] as const;

const CLEAR_ALL_OPTION: SortOrClearOption = { value: 'CLEAR_ALL', label: 'Clear all' } as const;

const isSortOrClearOption = <O extends { value: unknown; label: string }>(opt: O | SortOrClearOption): opt is SortOrClearOption => {
  return opt.value === 'UP' || opt.value === 'DOWN' || opt.value === 'CLEAR_ALL';
};

const CustomOption = (props: any) => {
  const isSortValue = props.value === 'UP' || props.value === 'DOWN';
  const isClearValue = props.value === 'CLEAR_ALL';

  return (
    <div>
      <components.Option {...props}>
        {!isSortValue && !isClearValue && (
          <input type="checkbox" checked={props.isSelected} onChange={() => null} className="mr-2 cursor-pointer" />
        )}
        <label className={`font-bold text-sm cursor-pointer ${isSortValue || isClearValue ? 'text-ow-secondary' : 'text-ow-darker-grey'}`}>
          {props.label}
        </label>
      </components.Option>
    </div>
  );
};

const CustomMultiValue = () => null;

const CustomValueContainer = (props: any) => {
  return (
    <div {...props.innerProps} className="mt-0.5">
      {props.header}
      {props.children}
    </div>
  );
};

const CustomDropdownIndicator = () => {
  return <img src="/icons/filter-sort.svg" alt="Filter / sort icon" className="ml-1" />;
};

export default function TableHeaderSortFilter<T, O extends { value: unknown; label: string }>({
  header,
  selectLabel = 'Select filters',
  sortField,
  options,
  selectedOptions,
  onSelectionChange,
  onSortChange,
  clearAllSetter,
  toOptions,
  fromOptions,
}: Readonly<{
  header: string;
  selectLabel?: string;
  sortField: string;

  options: readonly T[];

  selectedOptions: MultiValue<O>;

  onSelectionChange: (selected: T[]) => void;
  onSortChange: (field: string, sortDirection: 'UP' | 'DOWN') => void;

  clearAllSetter: SetterOrUpdater<T[]>;

  toOptions: (items: readonly T[]) => readonly O[];
  fromOptions: (items: MultiValue<O>) => T[];
}>) {
  type OptionUnion = O | SortOrClearOption;
  type Group = GroupBase<OptionUnion>;

  const formatGroupOptions = (items: readonly T[]): readonly Group[] => {
    const filterOptions = toOptions(items);

    return [
      {
        label: 'Sort:',
        options: SORT_OPTIONS,
      },
      {
        label: `${selectLabel}:`,
        options: [CLEAR_ALL_OPTION, ...filterOptions],
      },
    ];
  };

  const handleChange = (selected: MultiValue<OptionUnion>) => {
    const sortOrClear = selected.find(isSortOrClearOption);

    if (sortOrClear?.value === 'UP' || sortOrClear?.value === 'DOWN') {
      onSortChange(sortField, sortOrClear.value);
      return;
    }

    if (sortOrClear?.value === 'CLEAR_ALL') {
      clearAllSetter([]);
      return;
    }

    const filtersOnly = selected.filter((x): x is O => !isSortOrClearOption(x));
    onSelectionChange(fromOptions(filtersOnly));
  };

  const styles = {
    option: (provided: any) => ({
      ...provided,
      background: 'none',
      paddingBottom: 0,
      paddingTop: 0,
      marginTop: 10,
      marginBottom: 10,
      textTransform: 'none',
    }),
    indicatorSeparator: (provided: any) => ({
      ...provided,
      display: 'none',
    }),
    control: (provided: any) => ({
      ...provided,
      border: 'none',
      cursor: 'pointer',
      boxShadow: 'none',
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      color: '#2C6EF2', // ow-secondary,
    }),
    placeholder: (provided: any) => ({
      ...provided,
      display: 'none',
    }),
    menu: (provided: any) => ({
      ...provided,
      width: '300px',
    }),
    menuList: (provided: any) => {
      const optionCount = options.length;
      const menuHeight = Math.min(optionCount * 30, 500);
      return {
        ...provided,
        maxHeight: '500px',
        height: `${menuHeight + 168}px`,
        overflowY: 'auto',
        width: '300px',
      };
    },
    groupHeading: (provided: any) => ({
      ...provided,
      textTransform: 'none',
      fontSize: '0.75rem',
      color: '#000',
    }),
    group: (provided: any) => ({
      ...provided,
      paddingBottom: 0,
    }),
  };

  const renderCustomValueContainer = (props: any) => {
    return <CustomValueContainer header={header} {...props} />;
  };

  return (
    <Select<OptionUnion, true, Group>
      isMulti
      styles={styles}
      value={selectedOptions}
      onChange={handleChange}
      options={formatGroupOptions(options)}
      placeholder=""
      hideSelectedOptions={false}
      isClearable={false}
      isSearchable={false}
      components={{
        Option: CustomOption,
        MultiValue: CustomMultiValue,
        ValueContainer: renderCustomValueContainer,
        DropdownIndicator: CustomDropdownIndicator,
      }}
      className={'header-sort-filter-dropdown'}
      classNamePrefix={'header-sort-filter-dropdown'}
    />
  );
}
